import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import { iqServiceStatusBadgeVariant } from '@/components/features/merchants/iq-service-inventory/iq-service-inventory-status';
import { useIqServiceInventoryItem } from '@/hooks/use-iq-service-inventory';
import type { IqServiceInventoryStatus } from '@/types/iq-service-inventory';

export interface ChainInventoryAnnotationProps {
  /** Merchant des referenzierenden Service — Scope der By-Key-Abfrage. */
  merchantId?: string;
  /** Die referenzierte Inventar-Kennung (= `gatewayServiceId` des Chain-Eintrags). */
  gatewayServiceId: string;
  /** Status aus der status-ungefilterten Map (sofern der Key dort enthalten ist). */
  statusFromMap?: IqServiceInventoryStatus;
  /** (a)-Nutzungszaehler aus der Map (Eintrags-Zaehlung). */
  usageFromMap?: number;
  /** Map-Abfrage fertig geladen? Steuert, ob der By-Key-Fallback feuern darf. */
  mapSettled: boolean;
  /**
   * Die Inventar-Abfrage ist degradiert (List-Fehler / `usageUnavailable`). Dann rendert
   * die Komponente explizit „nicht ermittelbar" (Konsistenz mit dem Library-Badge) statt
   * den Zaehler still wegzulassen — und feuert KEINEN By-Key-Fallback (der liefe gegen
   * denselben degradierten Backend-Stand).
   */
  mapUnavailable?: boolean;
  /**
   * Spec 352 §6: die Inventar-Nutzungsableitung hat die Seiten-Schranke erreicht
   * (`usageTruncated`). Der Zaehler ist dann unzuverlaessig → die Komponente rendert
   * „nicht ermittelbar" statt einer (moeglicherweise zu niedrigen) Zahl. Der
   * Status-Badge bleibt, weil der Status der Kennung unabhaengig davon gilt.
   */
  mapTruncated?: boolean;
}

/**
 * Status-Badge + (a)-Nutzungszaehler fuer einen einzelnen FallbackChain-Eintrag
 * (Spec 347 §7). Aufloesung in zwei Stufen:
 *
 *  1. Steht der `gatewayServiceId` in der status-ungefilterten Map (`statusFromMap`),
 *     rendert die Komponente daraus direkt Badge + Zaehler — keine Netzabfrage.
 *  2. Fehlt der Key in der Map (realistisch: Pre-Backfill-Luecke — die FallbackChain
 *     referenziert eine Kennung, fuer die es noch keinen Inventar-Eintrag gibt), faellt
 *     die Komponente auf die By-Key-Detailabfrage zurueck. Erst aktiv, wenn die Map
 *     fertig geladen ist (sonst wuerde der Fallback gegen einen noch unvollstaendigen
 *     Stand entscheiden).
 *
 * Loest auch der Fallback nicht auf (404 / unregistriert / Service-Liste degradiert),
 * rendert die Komponente explizit "Nutzung nicht ermittelbar" — nie ein falsches
 * Aktiv/0 und nie stillschweigend nichts.
 *
 * Eine Hook-Instanz pro Chain-Zeile ist zulaessig: jede Zeile ist eine eigene
 * Komponenten-Instanz (kein Hook-in-Schleife).
 */
export function ChainInventoryAnnotation({
  merchantId,
  gatewayServiceId,
  statusFromMap,
  usageFromMap,
  mapSettled,
  mapUnavailable = false,
  mapTruncated = false,
}: ChainInventoryAnnotationProps) {
  const { t: tInv } = useTranslation('iq-service-inventory');

  // Spec 352 §6: bei Truncation ist der Zaehler unzuverlaessig → „nicht ermittelbar".
  const renderUnknownUsage = () => (
    <span className="text-[10px] text-muted-foreground">{tInv('usage.usageUnknown')}</span>
  );

  // Spec 352 §6: der (a)-Zaehler im Service-Kontext wird zum Drill-down in die
  // gefilterte /services-Sicht. `&merchant=` wird angehaengt, wo der Merchant bekannt
  // ist (Service-Detail kennt ihn); ohne Merchant bleibt der Zaehler Klartext.
  const usageHref = merchantId
    ? `/services?gw=${encodeURIComponent(gatewayServiceId)}&merchant=${encodeURIComponent(merchantId)}`
    : null;
  const renderUsageCount = (count: number) => {
    const text = tInv('usage.inEntries', { count });
    return usageHref ? (
      <Link
        to={usageHref}
        className="text-[10px] text-muted-foreground hover:text-primary hover:underline"
      >
        {text}
      </Link>
    ) : (
      <span className="text-[10px] text-muted-foreground">{text}</span>
    );
  };

  // Stufe 2 darf erst feuern, wenn die Map keinen Treffer hat, fertig geladen UND
  // NICHT degradiert ist (degradiert → kein weiterer By-Key-Call gegen denselben Stand).
  const byKeyEnabled = !statusFromMap && !mapUnavailable && mapSettled && !!merchantId;
  const byKey = useIqServiceInventoryItem(
    byKeyEnabled ? merchantId : undefined,
    byKeyEnabled ? gatewayServiceId : undefined,
  );

  // Stufe 1: Treffer in der Map → direkt rendern.
  if (statusFromMap) {
    return (
      <>
        <Badge variant={iqServiceStatusBadgeVariant(statusFromMap)} className="text-[10px]">
          {tInv(`status.${statusFromMap}`)}
        </Badge>
        {mapTruncated
          ? renderUnknownUsage()
          : typeof usageFromMap === 'number' && usageFromMap > 0 && renderUsageCount(usageFromMap)}
      </>
    );
  }

  // Degradiert (List-Fehler / usageUnavailable) → explizit "nicht ermittelbar",
  // konsistent mit dem Library-Badge; kein By-Key-Versuch.
  if (mapUnavailable) {
    return (
      <span className="text-[10px] text-muted-foreground">{tInv('usage.usageUnknown')}</span>
    );
  }

  // Map noch nicht fertig oder Fallback laeuft → still bleiben (kein Flackern).
  if (!byKeyEnabled || byKey.isLoading) {
    return null;
  }

  // Stufe 2 erfolgreich: Badge + Zaehler aus dem By-Key-Item.
  if (byKey.data) {
    const item = byKey.data;
    return (
      <>
        <Badge variant={iqServiceStatusBadgeVariant(item.status)} className="text-[10px]">
          {tInv(`status.${item.status}`)}
        </Badge>
        {item.usageTruncated
          ? renderUnknownUsage()
          : typeof item.fallbackUsageCount === 'number' &&
            item.fallbackUsageCount > 0 &&
            renderUsageCount(item.fallbackUsageCount)}
      </>
    );
  }

  // Fehler oder fertig-ohne-Daten → explizit "nicht ermittelbar".
  return (
    <span className="text-[10px] text-muted-foreground">{tInv('usage.usageUnknown')}</span>
  );
}
